import {PageHeader, PageWrapper} from '../../../Layout';
import {DataConfigCard} from '../../../components/shared/DataConfigCard';
import classes from './index.module.scss';
import RowItem from '../../../components/bloc/DailyReport/Day/RowItem';
import {useSearchParams} from 'react-router-dom';
import {useCleanUpByDate} from '../../../store/cleanUp/cleanUpSlice';
import {dateToString} from '../../../utils/date_helpers';
import {useDayReportCleanUpData} from '../../../hooks/useDayReportCleanUpData';
import {useGroupedNoCleaningRecords} from '../../../hooks/useGroupedNoCleaningRecords';
import {LoadingContainer} from '../../../components/UI/LoadingContainer';
import DownloadLink from '../../../components/UI/PdfReport/DownloadLink';
import {usePdfReportData} from '../../../hooks/usePdfReportData';
import {pdf} from '@react-pdf/renderer';
import DailyReportPDF from '../../../components/UI/PdfReport/DailyReportPDF';
import {TextButton} from '../../../components/UI/Button/Text';
import {format} from 'date-fns';
import {IPDFProject, IPDFReportData} from '../../../models/pdfReport';
import pdfIcon from '../../../assets/icons/icon-pdf.png';

const downloadProgramPdf = async (data: IPDFReportData, project: IPDFProject, date: string) => {
    const filename = `${project.name} - Report of ${format(new Date(date), 'MMMM do, yyyy')}.pdf`;
    const blob = await pdf(<DailyReportPDF data={{...data, projects: [project]}} />).toBlob();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

export const DailyReportsDay = () => {
    const [params] = useSearchParams();
    const selectedDate = params.get('date');
    const currentDay = dateToString(new Date());
    const date = selectedDate ?? currentDay;

    const [cleanUps, loadedCleanUps] = useCleanUpByDate(date);
    const [groupedCleanUps, programsLoaded] = useDayReportCleanUpData(cleanUps);
    const [noCleaningRecords, noCleaningRecordsLoaded] = useGroupedNoCleaningRecords(date);
    const [pdfData, pdfDataLoaded] = usePdfReportData(date);

    const loadedData = loadedCleanUps && programsLoaded && noCleaningRecordsLoaded;

    return (
        <PageWrapper>
            <PageHeader returnIcon title={date} />
            {loadedData ? (
                <>
                    <DownloadLink date={date} data={pdfData} dataLoaded={pdfDataLoaded} />
                    {groupedCleanUps?.map((cleanUp) => {
                        const project = pdfData.projects.find((p) => p.name === cleanUp.program);
                        return (
                            <div key={cleanUp.program} className={`${classes.container}`}>
                                <DataConfigCard
                                    readOnly
                                    title={cleanUp.program}
                                    headerActions={
                                        pdfDataLoaded && project ? (
                                            <TextButton
                                                label="Download Program PDF"
                                                icon={pdfIcon}
                                                onClick={() => downloadProgramPdf(pdfData, project, date)}
                                            />
                                        ) : undefined
                                    }
                                >
                                    <RowItem cleanUp={cleanUp} />
                                </DataConfigCard>
                            </div>
                        );
                    })}
                    {noCleaningRecords.map((record) => (
                        <div key={record.id} className={`${classes.container}`}>
                            <DataConfigCard readOnly title={record.program}>
                                <div className={classes['no_cleaning_record']}>
                                    No cleaning activities due to{' '}
                                    <span className={classes['no_cleaning_record__name']}>
                                        {record.reason}
                                    </span>
                                </div>
                            </DataConfigCard>
                        </div>
                    ))}
                </>
            ) : (
                <LoadingContainer />
            )}
        </PageWrapper>
    );
};
